import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import profileApi from '../../../../app/api/profileApi';
import cloudApi from '../../../../app/api/cloudApi';
import {
  PROFILE_MODES,
  getModeConfig,
  getDefaultProfileData,
  isFieldDisabled,
  isRetryablePictureInductionError,
  isSkillkoSourcedEdit,
  getChangedCriticalFields,
  canWriteProfileForm,
} from '../profileFormConfig';
import { validateProfile, validateDraft } from '../../../../utils/profileValidation';
import { useAvailableActions } from '../../../../hooks/useAvailableActions';
import { useTranslations } from '../../../../hooks/use-translations';

const toSnakeCase = (value) => value.replace(/([A-Z])/g, '_$1').toLowerCase();

const parseSkillkoIdFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/users\/(\d+)(?:\?|#|$)/);
  if (!match) return null;
  const id = parseInt(match[1], 10);
  return Number.isNaN(id) || id < 1 ? null : id;
};

/**
 * Unified hook for managing profile form state and operations
 * @param {string} mode - One of PROFILE_MODES values
 * @returns {object} Form state and handlers
 */
export const useProfileForm = (mode) => {
  const config = getModeConfig(mode);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();
  const { account } = useSelector((state) => state.app);
  const { canDeleteProfile, canEditProfile, canCreateProfile } = useAvailableActions();
  
  // Get URL params based on mode
  const { id, profileId, siteId, skillkoId, teamId, sitePassId } = useParams();
  const currentSiteId = siteId || id;
  
  // Check if this is a deleted profile from navigation state
  const isDeletedProfile = location.state?.isDeletedProfile || false;

  // Site name for the current site, shown in the header so it's always clear which site is being viewed
  const [siteName, setSiteName] = useState(null);

  useEffect(() => {
    if (!auth?.user?.access_token || !currentSiteId) {
      setSiteName(null);
      return;
    }
    cloudApi
      .getSite(auth.user.access_token, currentSiteId)
      .then((response) => {
        setSiteName(response.data?.name || null);
      })
      .catch((error) => {
        console.error('Error loading site:', error);
        setSiteName(null);
      });
  }, [auth?.user?.access_token, currentSiteId]);
  
  // Create/Skillko/SitePass writes need CREATE_PROFILE; edit writes need EDIT_PROFILE
  const effectiveCanEdit = canWriteProfileForm({
    mode,
    isDeletedProfile,
    canEditProfile,
    canCreateProfile,
  });
  
  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [profileData, setProfileData] = useState(getDefaultProfileData(mode));
  const [initialProfileData, setInitialProfileData] = useState({});
  const [image, setImage] = useState(null);
  const [initialImage, setInitialImage] = useState(null);
  const [isImageChanged, setIsImageChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [dynamicDisabledFields, setDynamicDisabledFields] = useState({});
  const [skillkoUrl, setSkillkoUrl] = useState(null);
  const [resolvedSkillkoId, setResolvedSkillkoId] = useState(null);
  const [skillkoRefreshLoading, setSkillkoRefreshLoading] = useState(false);
  const [forceSkillkoUpdateLoading, setForceSkillkoUpdateLoading] = useState(false);
  
  // Metadata
  const [metadata, setMetadata] = useState({
    companies: [],
    certificates: [],
    roles: [],
    access: [],
  });

  // Certificate form state (for Create and Edit modes)
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [certificateValidFrom, setCertificateValidFrom] = useState(dayjs());
  const [certificateExpiresAt, setCertificateExpiresAt] = useState(dayjs().add(1, 'year'));
  const [certificateErrors, setCertificateErrors] = useState({});
  const [certificateNumber, setCertificateNumber] = useState('');

  // Edit mode specific state
  const [isActivating, setIsActivating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Skillko critical-field edit confirmation state
  // pendingSkillkoSection: null | 'personalDetails' | 'nextOfKin' | 'employeeDetails'
  const [pendingSkillkoSection, setPendingSkillkoSection] = useState(null);
  const [pendingSkillkoFields, setPendingSkillkoFields] = useState([]);

  // Track the Skillko-calculated default expiry so we can detect manual changes
  const skillkoDefaultExpiryRef = useRef(null);

  const formRef = useRef(null);

  /**
   * Resolve a DOM node for a validation field key
   */
  const findFieldElement = useCallback((fieldName) => {
    const snakeCaseName = toSnakeCase(fieldName);
    const selectors = [
      `[data-profile-field="${fieldName}"]`,
      `[name="${fieldName}"]`,
      `[id="${fieldName}"]`,
      `[aria-label="${fieldName}"]`,
      `[name="${snakeCaseName}"]`,
      `[id="${snakeCaseName}"]`,
      `[aria-label="${snakeCaseName}"]`,
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
    }

    return null;
  }, []);

  /**
   * Scroll and focus a specific field by validation key
   */
  const scrollToErrorField = useCallback((fieldName) => {
    if (!fieldName) return;

    const fieldElement = findFieldElement(fieldName);

    if (!fieldElement) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const focusTarget = fieldElement.matches('input, textarea, select, button')
      ? fieldElement
      : fieldElement.querySelector('input, textarea, select, button');

    if (focusTarget?.focus) {
      setTimeout(() => focusTarget.focus({ preventScroll: true }), 100);
    }
  }, [findFieldElement]);

  /**
   * Load metadata and profile data based on mode
   */
  useEffect(() => {
    const loadData = async () => {
      setIsInitialLoading(true);
      
      try {
        // Load metadata first
        const metadataResponse = await profileApi.getCreateProfileMetadata(
          auth.user.access_token,
          currentSiteId,
          account.id
        );
        
        const updatedMetadata = {
          companies: metadataResponse.data.companies,
          certificates: metadataResponse.data.certificateTypes,
          roles: metadataResponse.data.jobTitles,
          access: metadataResponse.data.accessLevels,
        };
        setMetadata(updatedMetadata);

        // Load profile data based on mode
        if (mode === PROFILE_MODES.EDIT) {
          await loadEditProfileData(updatedMetadata);
        } else if (mode === PROFILE_MODES.SKILLKO) {
          await loadSkillkoProfileData(updatedMetadata);
        } else if (mode === PROFILE_MODES.SITEPASS) {
          await loadSitePassProfileData(updatedMetadata);
        } else {
          setIsInitialLoading(false);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        enqueueSnackbar('Failed to load data. Please try again.', { variant: 'error' });
        setIsInitialLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Load profile data for Edit mode
   */
  const loadEditProfileData = async (updatedMetadata) => {
    try {
      let response;
      
      // If we know this is a deleted profile (from navigation state), use deleted endpoint directly
      if (isDeletedProfile) {
        try {
          response = await profileApi.getDeletedProfileData(auth.user.access_token, {
            profileId: profileId,
            siteId: currentSiteId,
            accountId: account.id,
          });
        } catch (deletedError) {
          console.error('Failed to load deleted profile:', deletedError);
          enqueueSnackbar('Failed to load deleted profile data', { variant: 'error' });
          throw deletedError;
        }
      } else {
        // Try regular profile endpoint first
        try {
          response = await profileApi.getProfileData(auth.user.access_token, {
            profileId: profileId,
            siteId: currentSiteId,
            accountId: account.id,
          });
        } catch (error) {
          // If profile not found (any error that might indicate profile doesn't exist), try deleted profile endpoint (for Bosca admins)
          const errorMessage = error.message?.toLowerCase() || '';
          const errorData = error.response?.data?.toString().toLowerCase() || '';
          const isNotFoundError = 
            error.response?.status === 400 || 
            error.response?.status === 404 ||
            error.response?.status === 500 ||
            errorMessage.includes('sequence') || 
            errorMessage.includes('no elements') ||
            errorMessage.includes('not found') ||
            errorData.includes('sequence') ||
            errorData.includes('no elements') ||
            errorData.includes('not found');
          
          if (isNotFoundError) {
            try {
              console.log('Profile not found in regular endpoint, trying deleted profile endpoint...');
              response = await profileApi.getDeletedProfileData(auth.user.access_token, {
                profileId: profileId,
                siteId: currentSiteId,
                accountId: account.id,
              });
              console.log('Successfully loaded deleted profile data');
            } catch (deletedError) {
              console.error('Failed to load deleted profile:', deletedError);
              // If deleted endpoint also fails, throw the deleted error with more context
              enqueueSnackbar('Failed to load profile data', { variant: 'error' });
              throw deletedError;
            }
          } else {
            throw error;
          }
        }
      }

      const data = response.data;
      const fetchedProfileData = {
        firstName: data.firstName,
        surname: data.lastName,
        dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth) : null,
        phone: data.phoneNumber,
        company: updatedMetadata.companies.find((x) => x.id === data.contractingCompanyId) || null,
        role: updatedMetadata.roles.find((x) => x.id === data.jobTitleId) || null,
        email: data.emailAddress,
        nextOfKin: data.nextOfKin,
        nextOfKinPhone: data.nextOfKinPhoneNumber,
        access: data.accessLevelId,
        isAntiPassback: data.antiPassBackActive,
        certificates: (data.certificates || []).map(cert => ({
          ...cert,
          validFrom: cert.validFrom ? dayjs(cert.validFrom) : null,
          expiresAt: cert.expiresAt ? dayjs(cert.expiresAt) : null,
        })),
        gateAccess: data.firstName,
        validFrom: data.validFrom ? dayjs(data.validFrom) : null,
        expiresAt: data.expiresAt ? dayjs(data.expiresAt) : null,
        status: data.status,
        inductionErrorMessage: data.inductionErrorMessage,
        source: data.source,
        skillkoAutomaticExpiry: data.skillkoAutomaticExpiry ?? false,
      };

      setProfileData((pd) => ({ ...pd, ...fetchedProfileData }));
      setSkillkoUrl(data.skillkoUrl || null);
      setResolvedSkillkoId(
        data.skillkoId ?? parseSkillkoIdFromUrl(data.skillkoUrl) ?? null
      );

      // For Skillko-sourced profiles, store the current expiry as the default
      // so toggling "Auto" back on can revert to it
      if (data.source === 'Skillko' && fetchedProfileData.expiresAt) {
        skillkoDefaultExpiryRef.current = fetchedProfileData.expiresAt;
      }
      
      // Handle image data (mark as initial to track for editing)
      handleImageData(data.image, true);
      
      setInitialProfileData({ ...fetchedProfileData });
    } finally {
      setIsInitialLoading(false);
    }
  };

  /**
   * Load profile data for Skillko mode
   */
  const loadSkillkoProfileData = async (updatedMetadata) => {
    try {
      const response = await profileApi.getProfileFromSkillko(auth.user.access_token, {
        profileId: skillkoId,
        siteId: currentSiteId,
        accountId: account.id,
        teamId: teamId === 'null' ? null : teamId,
      });

      const rawProfileData = response.data;
      const hasFailedRules = rawProfileData.failedRules && rawProfileData.failedRules.length > 0;
      
      // If profile has failed rules, set expiry to 1 month from now
      const defaultExpiry = hasFailedRules
        ? dayjs().add(1, 'month')
        : (rawProfileData.defaultExpiryDate ? dayjs(rawProfileData.defaultExpiryDate) : dayjs().add(1, 'year'));

      // Store the Skillko-calculated default expiry so we can detect manual changes
      skillkoDefaultExpiryRef.current = defaultExpiry;

      // Look up values from metadata - only use if found
      const foundCompany = updatedMetadata.companies.find(x => x.id === rawProfileData.companyId);
      const foundRole = updatedMetadata.roles.find(x => x.id === rawProfileData.jobTitleId);

      setProfileData((pd) => ({
        ...pd,
        firstName: rawProfileData.firstName,
        surname: rawProfileData.lastName,
        email: rawProfileData.email,
        phone: rawProfileData.phoneNumber,
        dateOfBirth: rawProfileData.dateOfBirth ? dayjs(rawProfileData.dateOfBirth) : null,
        skillkoCertificates: rawProfileData.skillkoCertificates || [],
        expiresAt: defaultExpiry,
        skillkoAutomaticExpiry: true,
        role: foundRole,
        company: foundCompany,
        nextOfKin: rawProfileData.emergencyContactName || '',
        nextOfKinPhone: rawProfileData.emergencyContactNumber || '',
        failedRules: rawProfileData.failedRules || [],
      }));

      // Set dynamic disabled fields based on what data came from Skillko
      // Only disable fields if the value was actually found in metadata
      // (if ID exists but value not found, user needs to select a new value)
      setDynamicDisabledFields({
        firstName: !!rawProfileData.firstName,
        surname: !!rawProfileData.lastName,
        email: !!rawProfileData.email,
        phone: !!rawProfileData.phoneNumber,
        dateOfBirth: !!rawProfileData.dateOfBirth,
        nextOfKin: !!rawProfileData.emergencyContactName,
        nextOfKinPhone: !!rawProfileData.emergencyContactNumber,
        company: !!foundCompany,
        role: !!foundRole,
      });

      setSkillkoUrl(rawProfileData.skillkoUrl || null);

      // Only set image if imageUrl has actual content
      if (rawProfileData.imageUrl && rawProfileData.imageUrl.trim().length > 0) {
        setImage(`data:image/jpeg;base64,${rawProfileData.imageUrl}`);
      } else {
        setImage(null);
      }
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (mode === PROFILE_MODES.SKILLKO && skillkoId) {
      const id = parseInt(skillkoId, 10);
      setResolvedSkillkoId(Number.isNaN(id) || id < 1 ? null : id);
    }
  }, [mode, skillkoId]);

  const skillkoFieldLabels = () => ({
    FirstName: t('profiles.form.fieldFirstName'),
    LastName: t('profiles.form.fieldSurname'),
    DateOfBirth: t('profiles.form.fieldDateOfBirth'),
    PhoneNumber: t('profiles.form.fieldPhone'),
    NextOfKin: t('profiles.form.fieldNextOfKin'),
    NextOfKinPhoneNumber: t('profiles.form.fieldNextOfKinPhone'),
    Company: t('profiles.form.fieldCompany'),
    JobTitle: t('profiles.form.fieldRole'),
    Picture: t('profiles.form.fieldPicture'),
    ExpiryDate: t('profiles.form.fieldExpiry'),
  });

  const handleForceUpdateFromSkillko = async () => {
    if (mode !== PROFILE_MODES.EDIT || !profileId || !currentSiteId) {
      return;
    }

    setForceSkillkoUpdateLoading(true);
    try {
      const response = await cloudApi.forceUpdateRegisteredProfileFromSkillko(auth.user.access_token, {
        accountId: account.id,
        siteId: currentSiteId,
        profileId,
      });

      const updatedFields = response.data?.updatedFields || [];
      const labels = skillkoFieldLabels();
      const fieldList = updatedFields.map((field) => labels[field] || field);

      if (fieldList.length > 0) {
        enqueueSnackbar(t('profiles.form.forceUpdateFromSkillkoSuccess', { fields: fieldList.join(', ') }), {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(t('profiles.form.forceUpdateFromSkillkoUnchanged'), { variant: 'success' });
      }

      await loadEditProfileData(metadata);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.error || t('profiles.form.forceUpdateFromSkillkoFailed');
      enqueueSnackbar(typeof msg === 'string' ? msg : t('profiles.form.forceUpdateFromSkillkoFailed'), {
        variant: 'error',
      });
    } finally {
      setForceSkillkoUpdateLoading(false);
    }
  };

  const handleRefreshFromSkillko = async () => {
    if (!effectiveCanEdit) {
      return;
    }

    if (!resolvedSkillkoId || resolvedSkillkoId < 1) {
      enqueueSnackbar(t('profiles.form.refreshFromSkillkoFailed'), { variant: 'warning' });
      return;
    }

    setSkillkoRefreshLoading(true);
    try {
      await cloudApi.forceSyncSkillkoProfile(
        auth.user.access_token,
        account.id,
        resolvedSkillkoId
      );
      enqueueSnackbar(t('profiles.form.refreshFromSkillkoSuccess'), { variant: 'success' });

      if (mode === PROFILE_MODES.EDIT) {
        await loadEditProfileData(metadata);
      } else if (mode === PROFILE_MODES.SKILLKO) {
        await loadSkillkoProfileData(metadata);
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.error || t('profiles.form.refreshFromSkillkoFailed');
      enqueueSnackbar(typeof msg === 'string' ? msg : t('profiles.form.refreshFromSkillkoFailed'), {
        variant: 'error',
      });
    } finally {
      setSkillkoRefreshLoading(false);
    }
  };

  /**
   * Load profile data for SitePass mode
   */
  const loadSitePassProfileData = async (updatedMetadata) => {
    try {
      const response = await profileApi.getProfileFromSitePass(auth.user.access_token, {
        profileId: sitePassId,
        siteId: currentSiteId,
        accountId: account.id,
      });

      const rawProfileData = response.data;

      // Use inductionExpiry from SitePass if available, otherwise default to 1 year
      const defaultExpiry = rawProfileData.inductionExpiry
        ? dayjs(rawProfileData.inductionExpiry)
        : dayjs().add(1, 'year');

      // Build certificates from SitePass expiry dates (SafePass/CSR and Manual Handling)
      const sitePassCertificates = [];

      if (rawProfileData.safePassCsrExpiry) {
        const safePassType = updatedMetadata.certificates.find(
          (ct) => ct.name.toLowerCase() === 'safepass' || ct.name.toLowerCase() === 'safe pass'
        );
        if (safePassType) {
          sitePassCertificates.push({
            id: safePassType.id,
            typeId: safePassType.id,
            name: safePassType.name,
            number: '',
            validFrom: dayjs(),
            expiresAt: dayjs(rawProfileData.safePassCsrExpiry),
          });
        }
      }

      if (rawProfileData.manualHandlingExpiry) {
        const manualHandlingType = updatedMetadata.certificates.find(
          (ct) => ct.name.toLowerCase() === 'manual handling'
        );
        if (manualHandlingType) {
          sitePassCertificates.push({
            id: manualHandlingType.id,
            typeId: manualHandlingType.id,
            name: manualHandlingType.name,
            number: '',
            validFrom: dayjs(),
            expiresAt: dayjs(rawProfileData.manualHandlingExpiry),
          });
        }
      }

      setProfileData((pd) => ({
        ...pd,
        firstName: rawProfileData.firstName || '',
        surname: rawProfileData.lastName || '',
        email: rawProfileData.email || '',
        phone: rawProfileData.phone || '',
        expiresAt: defaultExpiry,
        company: updatedMetadata.companies.find(x => x.name?.toLowerCase() === rawProfileData.companyName?.toLowerCase()),
        sitePassUniqueId: rawProfileData.sitePassUniqueId || '',
        inductionExpiry: rawProfileData.inductionExpiry,
        certificates: sitePassCertificates,
      }));

      // Set dynamic disabled fields based on what data came from SitePass
      setDynamicDisabledFields({
        firstName: !!rawProfileData.firstName,
        surname: !!rawProfileData.lastName,
        email: !!rawProfileData.email,
        phone: !!rawProfileData.phone,
      });
    } finally {
      setIsInitialLoading(false);
    }
  };

  /**
   * Handle image data format conversion
   */
  const handleImageData = (imageData, isInitial = false) => {
    if (!imageData) {
      setImage(null);
      if (isInitial) setInitialImage(null);
      return;
    }

    let processedImage;
    if (imageData.startsWith('data:image')) {
      // Fix invalid MIME type
      if (imageData.startsWith('data:image64,')) {
        const base64Data = imageData.substring('data:image64,'.length);
        processedImage = `data:image/png;base64,${base64Data}`;
      } else {
        processedImage = imageData;
      }
    } else {
      // Raw base64 data
      const cleanBase64 = imageData.replace(/\s/g, '');
      processedImage = `data:image/png;base64,${cleanBase64}`;
    }
    
    setImage(processedImage);
    if (isInitial) setInitialImage(processedImage);
  };

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (field, value) => {
      const isEditAutoExpiryTurnOn =
        field === 'skillkoAutomaticExpiry' &&
        value === true &&
        mode === PROFILE_MODES.EDIT;

      const canFetchRuleExpiry =
        isEditAutoExpiryTurnOn &&
        Boolean(auth?.user?.access_token && account?.id && currentSiteId && profileId);

      setProfileData((prev) => {
        const updated = { ...prev, [field]: value };

        if (field === 'skillkoAutomaticExpiry' && value === true) {
          const isEditSkillko = mode === PROFILE_MODES.EDIT && prev.source === 'Skillko';
          if (!isEditSkillko && skillkoDefaultExpiryRef.current) {
            updated.expiresAt = skillkoDefaultExpiryRef.current;
          } else if (isEditSkillko && !canFetchRuleExpiry && skillkoDefaultExpiryRef.current) {
            updated.expiresAt = skillkoDefaultExpiryRef.current;
          }
        }

        return updated;
      });

      if (!canFetchRuleExpiry) {
        return;
      }

      const token = auth.user.access_token;
      void profileApi
        .getSkillkoAutomaticExpiryPreview(token, {
          profileId,
          siteId: currentSiteId,
          accountId: account.id,
        })
        .then((res) => {
          const iso = res.data?.suggestedExpiresAt;
          if (!iso) {
            setProfileData((p) => {
              if (p.source !== 'Skillko' || !p.skillkoAutomaticExpiry) {
                return p;
              }
              const fallback = skillkoDefaultExpiryRef.current;
              return fallback ? { ...p, expiresAt: fallback } : p;
            });
            return;
          }
          setProfileData((p) =>
            p.source === 'Skillko' && p.skillkoAutomaticExpiry
              ? { ...p, expiresAt: dayjs(iso) }
              : p
          );
        })
        .catch(() => {
          setProfileData((p) => {
            if (p.source !== 'Skillko' || !p.skillkoAutomaticExpiry) {
              return p;
            }
            const fallback = skillkoDefaultExpiryRef.current;
            return fallback ? { ...p, expiresAt: fallback } : p;
          });
        });
    },
    [mode, auth?.user?.access_token, account?.id, currentSiteId, profileId]
  );

  /**
   * Check if a field is disabled
   */
  const checkFieldDisabled = useCallback((fieldName) => {
    if (isDeletedProfile || !effectiveCanEdit) {
      return true;
    }
    return isFieldDisabled(mode, fieldName, dynamicDisabledFields);
  }, [mode, dynamicDisabledFields, isDeletedProfile, effectiveCanEdit]);

  /**
   * Navigation handlers
   */
  const handleBackClick = useCallback(() => {
    if (mode === PROFILE_MODES.CREATE) {
      navigate(-1);
    } else {
      navigate(`/profiles/${currentSiteId}`, { state: config.navigation.backState });
    }
  }, [navigate, currentSiteId, config.navigation.backState, mode]);

  /**
   * Scroll to first error
   */
  const scrollToFirstError = useCallback((errorObj) => {
    if (!Object.keys(errorObj).length) return;

    scrollToErrorField(Object.keys(errorObj)[0]);
  }, [scrollToErrorField]);

  /**
   * Apply section validation errors in Edit mode
   */
  const validateEditSection = useCallback((sectionErrors) => {
    if (Object.keys(sectionErrors).length > 0) {
      setErrors({ ...sectionErrors, _action: 'save' });
      scrollToFirstError(sectionErrors);
      return false;
    }

    setErrors({});
    return true;
  }, [scrollToFirstError]);

  /**
   * Validate profile for saving
   */
  const validateForSave = useCallback(() => {
    const newErrors = validateProfile(profileData, image);

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...newErrors, _action: 'save' });
      scrollToFirstError(newErrors);
      return false;
    }
    setErrors({});
    return true;
  }, [profileData, image, scrollToFirstError]);

  /**
   * Validate for draft saving
   */
  const validateForDraft = useCallback(() => {
    const newErrors = validateDraft(profileData);

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...newErrors, _action: 'draft' });
      scrollToFirstError(newErrors);
      return false;
    }
    setErrors({});
    return true;
  }, [profileData, scrollToFirstError]);

  /**
   * Validate for Skillko mode
   */
  const validateSkillko = useCallback(() => {
    const errors = {};

    // Image validation
    if (!image) {
      errors.image = 'Profile picture is required';
    } else {
      const trimmedImage = image.trim();
      if (trimmedImage === 'data:image64,' ||
        trimmedImage.endsWith('base64,') ||
        !trimmedImage.includes(',')) {
        errors.image = 'Profile picture data is invalid or empty. Please upload a valid picture.';
      } else {
        const parts = trimmedImage.split(',');
        if (parts.length < 2 || !parts[1] || parts[1].trim().length < 100) {
          errors.image = 'Profile picture data is too small or invalid. Please upload a valid picture.';
        }
      }
    }

    if (!profileData.email) {
      errors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(profileData.email)) {
      errors.email = 'Enter a valid email address';
    }

    if (!profileData.firstName) errors.firstName = 'First name is required';
    if (!profileData.surname) errors.surname = 'Surname is required';
    if (!profileData.dateOfBirth) errors.dateOfBirth = 'Date of Birth is required';
    if (!profileData.company) errors.company = 'Company is required';
    if (!profileData.access) errors.access = 'Access is required';
    if (!profileData.role) errors.role = 'Role is required';

    if (profileData.validFrom && profileData.expiresAt && profileData.validFrom.isAfter(profileData.expiresAt)) {
      errors.validFrom = 'Valid From date cannot be after Expires At date';
      errors.expiresAt = 'Expires At date cannot be before Valid From date';
    }

    if (Object.keys(errors).length > 0) {
      setErrors({ ...errors, _action: 'save' });
      scrollToFirstError(errors);
      return false;
    }
    setErrors({});
    return true;
  }, [profileData, image, scrollToFirstError]);

  /**
   * Validate for SitePass mode (less strict than Skillko)
   */
  const validateSitePass = useCallback(() => {
    const errors = {};

    // Image validation
    if (!image) {
      errors.image = 'Profile picture is required';
    }

    if (!profileData.email) {
      errors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(profileData.email)) {
      errors.email = 'Enter a valid email address';
    }

    if (!profileData.firstName) errors.firstName = 'First name is required';
    if (!profileData.surname) errors.surname = 'Surname is required';
    if (!profileData.access) errors.access = 'Access Level is required';

    if (profileData.validFrom && profileData.expiresAt && profileData.validFrom.isAfter(profileData.expiresAt)) {
      errors.validFrom = 'Valid From date cannot be after Expires At date';
      errors.expiresAt = 'Expires At date cannot be before Valid From date';
    }

    if (Object.keys(errors).length > 0) {
      setErrors({ ...errors, _action: 'save' });
      scrollToFirstError(errors);
      return false;
    }
    setErrors({});
    return true;
  }, [profileData, image, scrollToFirstError]);

  /**
   * Save profile (Create, Skillko, or SitePass mode)
   */
  const handleSaveProfile = useCallback(async () => {
    if (!canCreateProfile) {
      return;
    }

    let isValid;
    if (mode === PROFILE_MODES.SKILLKO) {
      isValid = validateSkillko();
    } else if (mode === PROFILE_MODES.SITEPASS) {
      isValid = validateSitePass();
    } else {
      isValid = validateForSave();
    }

    if (!isValid) return;

    setIsLoading(true);

    try {
      // For Skillko mode, map certificates
      let certificatesPayload = [];
      if (mode === PROFILE_MODES.SKILLKO) {
        certificatesPayload = (profileData.skillkoCertificates || [])
          .filter(cert => cert.active)
          .map(cert => {
            const certificateType = metadata.certificates.find(ct => ct.name.toLowerCase() === cert.name.toLowerCase());
            return {
              typeId: certificateType?.id,
              validFrom: dayjs(cert.validFrom).format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
              expiresAt: dayjs(cert.expiresAt).format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
              name: cert.name,
              number: null,
              integrationId: cert.integrationId,
            };
          })
          .filter(cert => cert.typeId);
      } else {
        certificatesPayload = profileData.certificates.map(x => ({
          typeId: x.id,
          validFrom: x.validFrom,
          expiresAt: x.expiresAt,
          name: x.name,
          number: x.number,
        }));
      }

      // Determine integrationId based on mode
      let integrationId;
      if (mode === PROFILE_MODES.SKILLKO) {
        integrationId = profileData.email;
      } else if (mode === PROFILE_MODES.SITEPASS) {
        integrationId = profileData.sitePassUniqueId;
      }

      await profileApi.createProfile(auth.user.access_token, {
        accountId: account.id,
        siteId: currentSiteId,
        firstName: profileData.firstName,
        lastName: profileData.surname,
        email: profileData.email,
        phoneNumber: profileData.phone,
        contractingCompanyId: profileData.company?.id,
        dateOfBirth: profileData.dateOfBirth ? profileData.dateOfBirth.format('YYYY-MM-DD') : null,
        accessLevelId: profileData.access,
        jobTitleId: profileData.role?.id,
        picture: image ? image.replace(/^data:image\/[a-zA-Z]+;base64,/, 'data:image64,') : null,
        nextofKinPhoneNumber: profileData.nextOfKinPhone,
        nextOfKinName: profileData.nextOfKin,
        isAntiPassback: profileData.isAntiPassback,
        validFrom: profileData.validFrom ? profileData.validFrom.format("YYYY-MM-DDThh:mm:ss.SSS") + "Z" : null,
        expiresAt: profileData.expiresAt ? profileData.expiresAt.format("YYYY-MM-DDThh:mm:ss.SSS") + "Z" : null,
        skillkoAutomaticExpiry: mode === PROFILE_MODES.SKILLKO ? (profileData.skillkoAutomaticExpiry ?? true) : false,
        certificates: certificatesPayload,
        source: config.api.source,
        integrationId,
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} has been added as a Profile`, {
        variant: 'success',
      });

      // Reset and navigate
      setProfileData(getDefaultProfileData(mode));
      setImage(null);
      
      // Navigate with appropriate state based on mode
      if (mode === PROFILE_MODES.SITEPASS) {
        navigate(`/profiles/${currentSiteId}`, { state: { openOnLocalProfiles: true } });
      } else {
        navigate(`/profiles/${currentSiteId}`);
      }
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [mode, canCreateProfile, validateSkillko, validateSitePass, validateForSave, profileData, metadata.certificates, image, auth.user.access_token, account.id, currentSiteId, config.api.source, enqueueSnackbar, navigate]);

  /**
   * Save as draft (Create mode only)
   */
  const handleSaveDraft = useCallback(async () => {
    if (!canCreateProfile) {
      return;
    }

    if (!validateForDraft()) return;

    setIsLoading(true);

    try {
      await profileApi.saveProfileDraft(auth.user.access_token, {
        accountId: account.id,
        siteId: currentSiteId,
        firstName: profileData.firstName,
        lastName: profileData.surname,
        email: profileData.email,
        phoneNumber: profileData.phone,
        contractingCompanyId: profileData.company?.id,
        dateOfBirth: profileData.dateOfBirth ? profileData.dateOfBirth.format('YYYY-MM-DD') : null,
        accessLevelId: profileData.access,
        jobTitleId: profileData.role?.id,
        picture: image ? image.replace(/^data:image\/[a-zA-Z]+;base64,/, 'data:image64,') : null,
        nextofKinPhoneNumber: profileData.nextOfKinPhone,
        nextOfKinName: profileData.nextOfKin,
        isAntiPassback: profileData.isAntiPassback,
        validFrom: profileData.validFrom,
        expiresAt: profileData.expiresAt,
        certificates: profileData.certificates.map(x => ({
          typeId: x.id,
          validFrom: x.validFrom,
          expiresAt: x.expiresAt,
          name: x.name,
          number: x.number,
        })),
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} has been saved as a draft`, {
        variant: 'success',
      });

      setProfileData(getDefaultProfileData(mode));
      setImage(null);
      navigate(`/profiles/${currentSiteId}`);
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [canCreateProfile, validateForDraft, profileData, image, auth.user.access_token, account.id, currentSiteId, enqueueSnackbar, navigate, mode]);

  // === EDIT MODE SPECIFIC HANDLERS ===

  /**
   * Save personal details section (Edit mode)
   */
  const handleSavePersonalDetails = useCallback(async () => {
    const validationErrors = {};

    if (!profileData.firstName) validationErrors.firstName = 'First name is required';
    if (!profileData.surname) validationErrors.surname = 'Surname is required';
    if (!profileData.dateOfBirth) validationErrors.dateOfBirth = 'Date of Birth is required';
    if (!profileData.phone) validationErrors.phone = 'Phone number is required';

    if (!validateEditSection(validationErrors)) return;

    try {
      await profileApi.editProfilePersonalDetails(auth.user.access_token, {
        profileId,
        accountId: account.id,
        siteId: currentSiteId,
        firstName: profileData.firstName,
        lastName: profileData.surname,
        phoneNumber: profileData.phone,
        dateOfBirth: profileData.dateOfBirth ? profileData.dateOfBirth.format('YYYY-MM-DD') : null,
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} Profile has been updated`, {
        variant: 'success',
      });

      setInitialProfileData((prev) => ({
        ...prev,
        firstName: profileData.firstName,
        surname: profileData.surname,
        phone: profileData.phone,
        dateOfBirth: profileData.dateOfBirth,
      }));
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    }
  }, [profileData, validateEditSection, auth.user.access_token, profileId, account.id, currentSiteId, enqueueSnackbar]);

  /**
   * Save emergency contact section (Edit mode)
   */
  const handleSaveNextOfKin = useCallback(async () => {
    try {
      await profileApi.editProfileNextOfKin(auth.user.access_token, {
        profileId,
        accountId: account.id,
        siteId: currentSiteId,
        nextOfKin: profileData.nextOfKin,
        nextOfKinPhoneNumber: profileData.nextOfKinPhone,
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} Profile has been updated`, {
        variant: 'success',
      });

      setInitialProfileData((prev) => ({
        ...prev,
        nextOfKin: profileData.nextOfKin,
        nextOfKinPhone: profileData.nextOfKinPhone,
      }));
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    }
  }, [auth.user.access_token, profileId, account.id, currentSiteId, profileData, enqueueSnackbar]);

  /**
   * Save employee details section (Edit mode)
   */
  const handleSaveEmployeeDetails = useCallback(async () => {
    const validationErrors = {};

    if (!profileData.company) validationErrors.company = 'Company is required';
    if (!profileData.role) validationErrors.role = 'Role is required';

    if (!validateEditSection(validationErrors)) return;

    try {
      await profileApi.editProfileEmployeeDetails(auth.user.access_token, {
        profileId,
        accountId: account.id,
        siteId: currentSiteId,
        jobTitleId: profileData.role?.id,
        companyId: profileData.company?.id,
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} Profile has been updated`, {
        variant: 'success',
      });

      setInitialProfileData((prev) => ({
        ...prev,
        role: profileData.role,
        company: profileData.company,
      }));
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    }
  }, [profileData, validateEditSection, auth.user.access_token, profileId, account.id, currentSiteId, enqueueSnackbar]);

  /**
   * Save access section (Edit mode)
   */
  const handleSaveAccess = useCallback(async () => {
    const validationErrors = {};

    if (!profileData.access) {
      validationErrors.access = 'Access is required';
    }

    if (profileData.validFrom && profileData.expiresAt && profileData.validFrom.isAfter(profileData.expiresAt)) {
      validationErrors.validFrom = 'Valid From date cannot be after Expires At date';
      validationErrors.expiresAt = 'Expires At date cannot be before Valid From date';
    }

    if (!validateEditSection(validationErrors)) return;

    try {
      await profileApi.editProfileAccess(auth.user.access_token, {
        profileId,
        accountId: account.id,
        siteId: currentSiteId,
        antiPassback: profileData.isAntiPassback,
        accessLevelId: profileData.access,
        validFrom: profileData.validFrom ? profileData.validFrom.format("YYYY-MM-DDThh:mm:ss.SSS") + "Z" : null,
        expiresAt: profileData.expiresAt ? profileData.expiresAt.format("YYYY-MM-DDThh:mm:ss.SSS") + "Z" : null,
        // Only send the flag for Skillko-source profiles
        ...(profileData.source === 'Skillko' ? { skillkoAutomaticExpiry: profileData.skillkoAutomaticExpiry } : {}),
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} Profile has been updated`, {
        variant: 'success',
      });

      setInitialProfileData((prev) => ({
        ...prev,
        isAntiPassback: profileData.isAntiPassback,
        access: profileData.access,
        validFrom: profileData.validFrom,
        expiresAt: profileData.expiresAt,
        skillkoAutomaticExpiry: profileData.skillkoAutomaticExpiry,
      }));
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    }
  }, [profileData, validateEditSection, auth.user.access_token, profileId, account.id, currentSiteId, enqueueSnackbar]);

  /**
   * Save certificates section (Edit mode)
   */
  const handleSaveCertificates = useCallback(async () => {
    try {
      await profileApi.updateProfileCertificates(auth.user.access_token, {
        profileId,
        accountId: account.id,
        siteId: currentSiteId,
        certificates: profileData.certificates.map(cert => ({
          typeId: cert.typeId,
          name: cert.name,
          number: cert.number,
          validFrom: cert.validFrom.format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
          expiresAt: cert.expiresAt.format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
          integrationId: cert.integrationId || null,
          source: cert.source || null,
        })),
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} Profile certificates have been updated`, {
        variant: 'success',
      });

      setInitialProfileData((prev) => ({
        ...prev,
        certificates: [...profileData.certificates],
      }));
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    }
  }, [auth.user.access_token, profileId, account.id, currentSiteId, profileData, enqueueSnackbar]);

  /**
   * Handle image change (stores locally, requires explicit save in Edit mode)
   */
  const handleImageChange = useCallback((data) => {
    setIsImageChanged(true);
    setImage(data);
  }, []);

  /**
   * Save profile picture (Edit mode)
   */
  const handleSaveProfilePicture = useCallback(async () => {
    const hasInductionError = isRetryablePictureInductionError(profileData.inductionErrorMessage);

    const apiCall = hasInductionError
      ? profileApi.retryRegistrationProfilePicture
      : profileApi.editProfilePicture;

    const successMessage = hasInductionError
      ? 'Profile picture retry initiated successfully'
      : 'Profile picture updated successfully';

    const transformedPicture = image ? image.replace(/^data:image\/[a-zA-Z]+;base64,/, 'data:image64,') : null;

    try {
      await apiCall(auth.user.access_token, {
        profileId,
        siteId: currentSiteId,
        accountId: account.id,
        picture: transformedPicture,
      });
      
      enqueueSnackbar(successMessage, { variant: 'success' });
      setProfileData((prev) => ({ ...prev, inductionErrorMessage: null }));
      setInitialImage(image);
    } catch (err) {
      const errorMessage = hasInductionError
        ? 'Failed to retry profile picture processing'
        : 'Failed to update profile picture';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error(err);
    }
  }, [profileData.inductionErrorMessage, auth.user.access_token, profileId, currentSiteId, account.id, image, enqueueSnackbar]);

  /**
   * Delete profile (Edit mode)
   */
  const handleDeleteProfile = useCallback(async () => {
    setDeleteDialogOpen(false);
    try {
      await profileApi.deleteProfile(auth.user.access_token, {
        profileId,
        siteId: currentSiteId,
        accountId: account.id,
      });

      enqueueSnackbar(`${profileData.firstName} ${profileData.surname} Profile has been deleted`, {
        variant: 'success',
      });

      navigate(-1);
    } catch (error) {
      enqueueSnackbar('Something went wrong!', { variant: 'error' });
      console.error(error);
    }
  }, [auth.user.access_token, profileId, currentSiteId, account.id, profileData.firstName, profileData.surname, enqueueSnackbar, navigate]);

  /**
   * Activate profile draft (Edit mode)
   */
  const handleActivateProfile = useCallback(async () => {
    const validationErrors = validateProfile(profileData, image);

    if (Object.keys(validationErrors).length > 0) {
      setErrors({ ...validationErrors, _action: 'activate' });
      enqueueSnackbar('Please fill in all required fields before activating the profile', {
        variant: 'error',
      });
      return;
    }

    setIsActivating(true);
    try {
      await profileApi.activateProfileDraft(auth.user.access_token, {
        profileId,
        siteId: currentSiteId,
        accountId: account.id,
      });

      enqueueSnackbar('Profile activated successfully', { variant: 'success' });
      window.location.href = `/profiles/${currentSiteId}`;
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to activate profile', { variant: 'error' });
    } finally {
      setIsActivating(false);
      setErrors({});
    }
  }, [profileData, image, auth.user.access_token, profileId, currentSiteId, account.id, enqueueSnackbar, navigate]);

  // === CERTIFICATE MANAGEMENT ===

  /**
   * Add a certificate
   */
  const handleAddCertificate = useCallback(() => {
    const certErrors = {};

    if (!selectedCertificate) {
      certErrors.certificate = 'Certificate is required';
    }

    if (certificateValidFrom && certificateExpiresAt && certificateValidFrom.isAfter(certificateExpiresAt)) {
      certErrors.validFrom = 'Valid From date cannot be after Expires At date';
      certErrors.expiresAt = 'Expires At date cannot be before Valid From date';
    }

    setCertificateErrors(certErrors);

    if (Object.keys(certErrors).length > 0) {
      return;
    }

    const newCertificate = {
      id: selectedCertificate.id,
      typeId: selectedCertificate.id,
      name: selectedCertificate.name,
      number: certificateNumber,
      validFrom: certificateValidFrom,
      expiresAt: certificateExpiresAt,
    };

    setProfileData((prev) => ({
      ...prev,
      certificates: [...prev.certificates, newCertificate],
    }));

    // Reset form
    setSelectedCertificate(null);
    setCertificateNumber('');
    setCertificateValidFrom(dayjs());
    setCertificateExpiresAt(dayjs().add(1, 'year'));
  }, [selectedCertificate, certificateNumber, certificateValidFrom, certificateExpiresAt]);

  /**
   * Remove a certificate
   */
  const handleRemoveCertificate = useCallback((certificateToRemove) => {
    const idField = mode === PROFILE_MODES.EDIT ? 'typeId' : 'id';
    setProfileData((prev) => ({
      ...prev,
      certificates: prev.certificates.filter(cert => cert[idField] !== certificateToRemove[idField]),
    }));
  }, [mode]);

  // === METADATA HELPERS ===

  /**
   * Add new role to metadata
   */
  const handleAddNewRole = useCallback((value) => {
    if (metadata.roles?.find(x => x.name.toLowerCase() === value.toLowerCase())) {
      return;
    }

    const newRole = { id: value, name: value };
    setMetadata((md) => ({
      ...md,
      roles: [...md.roles, newRole],
    }));
    handleInputChange('role', newRole);
  }, [metadata.roles, handleInputChange]);

  /**
   * Add new company to metadata
   */
  const handleAddNewCompany = useCallback((value) => {
    if (metadata.companies?.find(x => x.name === value)) {
      return;
    }

    const newCompany = { id: value, name: value };
    setMetadata((md) => ({
      ...md,
      companies: [...md.companies, newCompany],
    }));
    handleInputChange('company', newCompany);
  }, [metadata.companies, handleInputChange]);

  /**
   * Add new certificate type to metadata
   */
  const handleAddNewCertificateType = useCallback((value) => {
    if (metadata.certificates?.find(x => x.name === value)) {
      return;
    }

    const newCertificate = { id: null, name: value };
    setMetadata((md) => ({
      ...md,
      certificates: [...md.certificates, newCertificate],
    }));
    setSelectedCertificate(newCertificate);
  }, [metadata.certificates]);

  // === SECTION EDIT STATUS CHECKS (for Edit mode) ===

  const isPersonalDetailsEdited = useCallback(() => {
    return (
      initialProfileData.firstName !== profileData.firstName ||
      initialProfileData.surname !== profileData.surname ||
      initialProfileData.dateOfBirth !== profileData.dateOfBirth ||
      initialProfileData.phone !== profileData.phone
    );
  }, [initialProfileData, profileData]);

  const isNextOfKinEdited = useCallback(() => {
    return (
      initialProfileData.nextOfKin !== profileData.nextOfKin ||
      initialProfileData.nextOfKinPhone !== profileData.nextOfKinPhone
    );
  }, [initialProfileData, profileData]);

  const isEmployeeDetailsEdited = useCallback(() => {
    return (
      initialProfileData.company !== profileData.company ||
      initialProfileData.role !== profileData.role
    );
  }, [initialProfileData, profileData]);

  const isAccessEdited = useCallback(() => {
    return (
      initialProfileData.access !== profileData.access ||
      initialProfileData.validFrom !== profileData.validFrom ||
      initialProfileData.expiresAt !== profileData.expiresAt ||
      initialProfileData.isAntiPassback !== profileData.isAntiPassback ||
      initialProfileData.skillkoAutomaticExpiry !== profileData.skillkoAutomaticExpiry
    );
  }, [initialProfileData, profileData]);

  const isCertificatesEdited = useCallback(() => {
    if (initialProfileData.certificates?.length !== profileData.certificates?.length) {
      return true;
    }
    return JSON.stringify(initialProfileData.certificates) !== JSON.stringify(profileData.certificates);
  }, [initialProfileData.certificates, profileData.certificates]);

  const isPictureEdited = useCallback(() => {
    return image !== initialImage;
  }, [image, initialImage]);

  // === SKILLKO CRITICAL FIELD EDIT CONFIRMATION (Edit mode) ===

  /**
   * Dispatches to the actual save handler for a given section key.
   */
  const runSkillkoSectionSave = useCallback((sectionKey) => {
    switch (sectionKey) {
      case 'personalDetails':
        return handleSavePersonalDetails();
      case 'nextOfKin':
        return handleSaveNextOfKin();
      case 'employeeDetails':
        return handleSaveEmployeeDetails();
      case 'picture':
        return handleSaveProfilePicture();
      default:
        return undefined;
    }
  }, [handleSavePersonalDetails, handleSaveNextOfKin, handleSaveEmployeeDetails, handleSaveProfilePicture]);

  /**
   * Wraps a section's save handler so that, for Skillko-sourced profiles in Edit mode,
   * saving a section with changed Skillko-critical fields first shows a confirmation
   * dialog instead of saving immediately. The picture section doesn't have field-level
   * data in `profileData`, so its "changed" check uses `isPictureEdited` instead of
   * `getChangedCriticalFields`.
   */
  const requestSaveSection = useCallback((sectionKey) => {
    if (isSkillkoSourcedEdit(mode, profileData)) {
      if (sectionKey === 'picture') {
        if (isPictureEdited()) {
          setPendingSkillkoFields(['image']);
          setPendingSkillkoSection('picture');
          return;
        }
      } else {
        const changedFields = getChangedCriticalFields(sectionKey, profileData, initialProfileData);
        if (changedFields.length > 0) {
          setPendingSkillkoFields(changedFields);
          setPendingSkillkoSection(sectionKey);
          return;
        }
      }
    }
    runSkillkoSectionSave(sectionKey);
  }, [mode, profileData, initialProfileData, isPictureEdited, runSkillkoSectionSave]);

  const requestSavePersonalDetails = useCallback(() => requestSaveSection('personalDetails'), [requestSaveSection]);
  const requestSaveNextOfKin = useCallback(() => requestSaveSection('nextOfKin'), [requestSaveSection]);
  const requestSaveEmployeeDetails = useCallback(() => requestSaveSection('employeeDetails'), [requestSaveSection]);
  const requestSaveProfilePicture = useCallback(() => requestSaveSection('picture'), [requestSaveSection]);

  /**
   * User acknowledged the warning and wants to save anyway.
   */
  const confirmSkillkoEdit = useCallback(() => {
    const section = pendingSkillkoSection;
    setPendingSkillkoSection(null);
    setPendingSkillkoFields([]);
    runSkillkoSectionSave(section);
  }, [pendingSkillkoSection, runSkillkoSectionSave]);

  /**
   * User cancelled out of the warning dialog - discard the pending save.
   */
  const cancelSkillkoEdit = useCallback(() => {
    setPendingSkillkoSection(null);
    setPendingSkillkoFields([]);
  }, []);

  return {
    // Config
    mode,
    config,

    // State
    isLoading,
    isInitialLoading,
    profileData,
    setProfileData,
    initialProfileData,
    image,
    setImage: mode === PROFILE_MODES.EDIT ? handleImageChange : setImage,
    isImageChanged,
    errors,
    setErrors,
    dynamicDisabledFields,
    metadata,
    setMetadata,
    skillkoUrl,
    resolvedSkillkoId,
    skillkoRefreshLoading,
    forceSkillkoUpdateLoading,
    formRef,

    // Certificate form state
    selectedCertificate,
    setSelectedCertificate,
    certificateValidFrom,
    setCertificateValidFrom,
    certificateExpiresAt,
    setCertificateExpiresAt,
    certificateErrors,
    setCertificateErrors,
    certificateNumber,
    setCertificateNumber,

    // Edit mode specific state
    isActivating,
    deleteDialogOpen,
    setDeleteDialogOpen,

    // Skillko critical-field edit confirmation state
    pendingSkillkoSection,
    pendingSkillkoFields,
    confirmSkillkoEdit,
    cancelSkillkoEdit,

    // Permissions
    canDeleteProfile,
    canEditProfile: effectiveCanEdit,
    canCreateProfile,
    isDeletedProfile,

    // Params
    currentSiteId,
    siteName,
    profileId,
    
    // Auth & Account (for tabs)
    auth,
    account,

    // Handlers
    handleInputChange,
    checkFieldDisabled,
    handleBackClick,
    scrollToErrorField,
    handleRefreshFromSkillko,
    handleForceUpdateFromSkillko,

    // Save handlers
    handleSaveProfile,
    handleSaveDraft,
    handleSavePersonalDetails,
    handleSaveNextOfKin,
    handleSaveEmployeeDetails,
    handleSaveAccess,
    handleSaveCertificates,
    handleSaveProfilePicture,

    // Skillko critical-field aware save requests (use these as onSave in the UI)
    requestSavePersonalDetails,
    requestSaveNextOfKin,
    requestSaveEmployeeDetails,
    requestSaveProfilePicture,

    // Edit mode handlers
    handleDeleteProfile,
    handleActivateProfile,

    // Certificate handlers
    handleAddCertificate,
    handleRemoveCertificate,

    // Metadata handlers
    handleAddNewRole,
    handleAddNewCompany,
    handleAddNewCertificateType,

    // Edit status checks
    isPersonalDetailsEdited,
    isNextOfKinEdited,
    isEmployeeDetailsEdited,
    isAccessEdited,
    isCertificatesEdited,
    isPictureEdited,
  };
};

export default useProfileForm;

